import React from "react";
import { Tooltip, Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { UPDATE_STATE } from "../../../../actions/types";
import { getPermissions } from "../../../../app/util";
import dataService from "../../../../services/data.service";
import { analytics } from "../../../../hooks/use-analytics";

import { VCAP_NON_APPLICABLE_TILES } from '../../../../app/config-v4-api';

const getVcCookieKey = (userId) => `lodgiq_vc_${userId}`;

const writeCookie = (key, value, years = 1) => {
    const expiry = new Date();
    expiry.setFullYear(expiry.getFullYear() + years);
    document.cookie = `${key}=${value}; expires=${expiry.toUTCString()}; path=/; SameSite=Lax`;
};

const writeVcCookie = (userId, value) => writeCookie(getVcCookieKey(userId), value ? "1" : "0");

const STYLES = `
  @keyframes capacityBounceOn {
    0%   { transform: scale(1); }
    50%  { transform: scale(1.08); }
    100% { transform: scale(1); }
  }
  @keyframes capacityBounceOff {
    0%   { transform: scale(1); }
    50%  { transform: scale(0.94); }
    100% { transform: scale(1); }
  }
`;

const svgStyle = (active) => ({
    fill: active ? "#fff" : "#606060",
    background: active ? "#1976d2" : "#EBEBEB",
    borderRadius: "50px",
    transition: "background 0.4s ease, fill 0.4s ease",
    animation: active
        ? "capacityBounceOn 0.4s ease-out"
        : "capacityBounceOff 0.35s ease-out",
    boxShadow: active ? "0 2px 8px rgba(25,118,210,0.4)" : "none",
});

// ── Bed icon ─────────────────────────────────────────────────────────────────
const BedIcon = ({ active }) => (
    <svg
        key={`bed-${active}`}
        className="capacity-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="30"
        height="30"
        viewBox="0 0 256 256"
        style={{ ...svgStyle(active), padding: '5px', boxSizing: 'border-box' }}
    >
        <path d="M216,112H184V72a16,16,0,0,0-16-16H88A16,16,0,0,0,72,72v40H40a8,8,0,0,0-8,8v80a8,8,0,0,0,16,0V184h160v16a8,8,0,0,0,16,0V120A8,8,0,0,0,216,112ZM88,72h80v40H88ZM48,168V128H208v40Z" />
    </svg>
);

// ─────────────────────────────────────────────────────────────────────────────

const CapacityButton = () => {
    const dispatch = useDispatch();
    const { ui_state, app_state } = useSelector((state) => state.app.current_state);
    const { user, profile } = useSelector((state) => state.auth);
    const userId = user?.id || user?.email;
    const { permissions } = getPermissions(profile.property_details, app_state.property_id);
    const variable_capacity_enabled = permissions?.variable_capacity_enabled;
    const active = variable_capacity_enabled && (ui_state.showVariableCapacity ?? false);
    const isNotApplicableTile = ui_state.active_tiles?.every(t => VCAP_NON_APPLICABLE_TILES.includes(t));
    // Hide on the Budget tile whenever there's nothing to toggle capacity against — either
    // budget isn't enabled (permissions.budget === 0 → "Budget is not uploaded" info) or it's
    // enabled but no budget data is present (empty "GetBudget" series → "Data not available" info).
    // Mirrors the chart/download icons, which also disappear in these empty states.
    const budgetData = dataService.getSeriesById("GetBudget")?.data?.data;
    const isBudgetEmpty = !Array.isArray(budgetData) || budgetData.length <= 0;
    const isBudgetWithoutData =
        ui_state.active_tiles?.[0] === "budget" && (permissions?.budget === 0 || isBudgetEmpty);

    if (!variable_capacity_enabled || isNotApplicableTile || isBudgetWithoutData) return null;

    const handleToggle = () => {
        const newValue = !active;
        writeVcCookie(userId, newValue);
        dispatch({
            type: UPDATE_STATE,
            payload: { ui_state: { showspinner: true, showVariableCapacity: newValue } },
        });
        // Tracked the same way as the chart/table view switch (gridViewButton):
        // routed through "tile_change" so it lands on the active tile's own event,
        // with the toggle state riding along as a property.
        analytics.track("tile_change", {
            "selected_tile": ui_state.active_tiles.join(),
            variable_capacity: newValue ? 'on' : 'off',
        });
        setTimeout(() => {
            dispatch({
                type: UPDATE_STATE,
                payload: { ui_state: { showspinner: false } },
            });
        }, 1000);
    };

    return (
        <>
            <style>{STYLES}</style>

            <Tooltip
                followCursor
                title={active
                    ? "OOO/OOS rooms excluded from Occ% & RevPAR — reflecting true available inventory"
                    : "Toggle on to exclude Out-of-Order (OOO) and Out-of-Service (OOS) rooms from Occ% & RevPAR"
                }
                componentsProps={{
                    tooltip: {
                        sx: {
                            fontSize: '11px',
                            maxWidth: 220,
                        },
                    },
                }}
            >
                <Box
                    onClick={handleToggle}
                    role="switch"
                    aria-checked={!!active}
                    sx={{
                        display: 'flex',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover svg': {
                            boxShadow: '0 1px 3px rgba(0,0,0,0.24), 0 1px 2px rgba(0,0,0,0.12)',
                        },
                    }}
                >
                    <BedIcon active={active} />
                </Box>
            </Tooltip>
        </>
    );
};

export default CapacityButton;
